import { router } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { Button } from '@/src/components/Button';
import { useToast } from '@/src/components/Toast';
import { useAuth } from '@/src/features/auth/use-auth';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

type FacebookCallbackParams = {
  accessToken: string | null;
  error: string | null;
};

type CallbackState = 'loading' | 'error';

function readFirstParam(
  queryParams: URLSearchParams,
  hashParams: URLSearchParams,
  key: string,
) {
  return hashParams.get(key) ?? queryParams.get(key);
}

function parseFacebookCallbackParams(): FacebookCallbackParams {
  if (typeof window === 'undefined') {
    return {
      accessToken: null,
      error: 'Facebook sign-in can only be completed in a browser window.',
    };
  }

  const queryParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash);
  const error =
    readFirstParam(queryParams, hashParams, 'error_description') ??
    readFirstParam(queryParams, hashParams, 'error_message') ??
    readFirstParam(queryParams, hashParams, 'error_reason') ??
    readFirstParam(queryParams, hashParams, 'error');

  return {
    accessToken: readFirstParam(queryParams, hashParams, 'access_token'),
    error,
  };
}

function clearSensitiveCallbackURL() {
  if (typeof window === 'undefined') {
    return;
  }

  window.history.replaceState(null, document.title, '/auth/facebook/callback');
}

export default function FacebookAuthCallbackScreen() {
  const didRunRef = useRef(false);
  const { signInWithFacebook } = useAuth();
  const { showError, showSuccess } = useToast();
  const [state, setState] = useState<CallbackState>('loading');
  const [message, setMessage] = useState('Completing Facebook sign-in...');

  useEffect(() => {
    if (didRunRef.current) {
      return;
    }

    didRunRef.current = true;

    async function completeFacebookSignIn() {
      const { accessToken, error } = parseFacebookCallbackParams();

      if (error) {
        clearSensitiveCallbackURL();
        setState('error');
        setMessage(error);
        return;
      }

      if (!accessToken) {
        setState('error');
        setMessage(
          'Facebook did not return a sign-in token. Please return to sign in and try again.',
        );
        return;
      }

      clearSensitiveCallbackURL();

      try {
        const result = await signInWithFacebook(accessToken);

        if (result.isNewUser) {
          showSuccess(
            'Finish your profile to complete your Facebook account setup.',
            'Facebook account created',
          );
        }

        router.replace('/home');
      } catch (caughtError) {
        setState('error');
        setMessage('Unable to continue with Facebook. Please try again.');
        showError(caughtError, 'Unable to continue with Facebook.');
      }
    }

    void completeFacebookSignIn();
  }, [showError, showSuccess, signInWithFacebook]);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{message}</Text>
      {state === 'error' ? (
        <Button
          title="Back to sign in"
          onPress={() => {
            router.replace('/sign-in');
          }}
          style={styles.button}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  button: {
    alignSelf: 'stretch',
    marginTop: spacing.lg,
  },
  container: {
    alignItems: 'center',
    backgroundColor: colors.background,
    flex: 1,
    justifyContent: 'center',
    padding: spacing.xl,
  },
  title: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '800',
    textAlign: 'center',
  },
});
